import { useState, useEffect } from 'react';
import { PageHeader } from '@/components/mypage/PageHeader';
import { AccordionItem } from '@/components/mypage/AccordionItem';
import { useToast } from '@/components/common/Toast';
import { useLoadingCoordinator, SkeletonComponents } from '@/hooks/useLoadingCoordinator';
import { sellerNoticesService } from '@/services/sellerNotices';
import type { Notice } from '@/types';

export function SellerNoticesPage() {
  const { showToast, ToastContainer } = useToast();
  const [notices, setNotices] = useState<Notice[]>([]);
  const [isDataLoading, setIsDataLoading] = useState(true);

  const { 
    shouldShowLoading, 
    shouldShowSkeleton, 
    shouldShowContent 
  } = useLoadingCoordinator(isDataLoading, {
    useSkeletonMode: true,
    minLoadingTime: 200
  });

  useEffect(() => {
    const fetchNotices = async () => {
      try {
        setIsDataLoading(true);
        const result = await sellerNoticesService.getAllNotices();
        
        if (result.success && result.data) {
          // Transform seller notices to match Notice type
          const transformedNotices: Notice[] = result.data.map(notice => ({
            id: notice.id,
            title: notice.title,
            content: notice.content,
            priority: notice.priority || 0,
            is_active: notice.is_active,
            created_at: notice.created_at,
            updated_at: notice.updated_at
          }));
          
          setNotices(transformedNotices);
        } else {
          showToast(result.error || '공지사항을 불러오는데 실패했습니다');
        }
      } catch (error) {
        console.error('Error fetching notices:', error);
        showToast('공지사항을 불러오는데 실패했습니다');
      } finally {
        setIsDataLoading(false);
      }
    };

    fetchNotices();
  }, [showToast]);

  // 전체 화면 로딩 (인증 로딩)
  if (shouldShowLoading) {
    return (
      <div className="min-h-screen bg-white">
        <PageHeader title="공지사항" />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
            <p className="text-gray-600">공지사항을 불러오는 중...</p>
          </div>
        </div>
      </div>
    );
  }

  // 스켈레톤 로딩 (데이터 로딩)
  if (shouldShowSkeleton) {
    return (
      <div className="min-h-screen bg-white">
        <PageHeader title="공지사항" />
        <SkeletonComponents.AccordionList count={4} />
      </div>
    );
  }

  // 콘텐츠 표시
  if (shouldShowContent) {
    return (
      <div className="min-h-screen bg-white">
        <PageHeader title="공지사항" />
        
        <div className="pb-6">
          {notices.length === 0 ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 text-gray-300">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
                    <path 
                      d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <p className="text-gray-600 mb-2">등록된 공지사항이 없습니다</p>
                <p className="text-gray-500 text-sm">새로운 공지사항이 등록되면 알려드릴게요</p>
              </div>
            </div>
          ) : (
            <div className="bg-white">
              {notices.map((notice) => (
                <AccordionItem key={notice.id} notice={notice} />
              ))}
            </div>
          )}
        </div>
        
        <ToastContainer />
      </div>
    );
  }

  // 폴백 (예상되지 않는 상태)
  return null;
}